/**
 * ChallengeHeaderV2 - Unified header bar for challenge session
 *
 * Features:
 * - Challenge name and stage indicator
 * - Timer display
 * - Navigation controls (Previous/Next)
 * - Progress indicator
 * - Review button
 * - Focus mode toggle
 * - Desmos graphing calculator (for math challenges)
 */

import { memo, useCallback, useState, lazy, Suspense } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectStageProgress,
  selectFormattedStageTime,
  selectProgress,
  selectCanNavigateNext,
  selectCanNavigatePrevious,
  selectAllAnswered,
  selectIsAdaptiveStage,
  selectIsLastQuestion,
  selectHasAnswer,
  selectUnansweredCount,
  selectOtherUnansweredCount,
  selectHasLeftLastQuestion
} from '../../hooks/useChallenge'
import { useAuthStore } from '../../stores/authStore'
import TimerDisplayV2 from './navigation/TimerDisplayV2'
import { useCalculatorOverlay } from '../DesmosCalculatorOverlay'

// Lazy load the calculator overlay and reference sheet
const DesmosCalculatorOverlay = lazy(() => import('../DesmosCalculatorOverlay'))
const MathReferenceSheet = lazy(() => import('../MathReferenceSheet'))

// Icons
const FocusIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
    />
  </svg>
)

const MinimizeIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
    />
  </svg>
)

const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
)

const ChevronRightIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

const GridIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
    />
  </svg>
)

const PauseIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
)

const CalculatorIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16"
    />
  </svg>
)

const ReferenceSheetIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
)

const ChallengeHeaderV2 = memo(() => {
  // Auth state for impersonation banner offset
  const isImpersonating = useAuthStore((state) => state.isImpersonating)

  // Calculator overlay state (keyboard shortcut Ctrl+G)
  const {
    isOpen: isCalculatorOpen,
    toggle: toggleCalculator,
    close: closeCalculator
  } = useCalculatorOverlay()

  // Reference sheet state (math challenges only)
  const [isReferenceSheetOpen, setIsReferenceSheetOpen] = useState(false)

  // Store state
  const currentChallenge = useChallengeStoreV2((state) => state.currentChallenge)
  const isMathChallenge = currentChallenge?.settings?.strategyType?.includes('MATH')
  const currentStage = useChallengeStoreV2((state) => state.currentStage)
  const stageProgress = useChallengeStoreV2(selectStageProgress)
  const progress = useChallengeStoreV2(selectProgress)
  const isFocusMode = useChallengeStoreV2((state) => state.isFocusMode)
  const currentQuestionIndex = useChallengeStoreV2((state) => state.currentQuestionIndex)

  // Navigation state
  const canNavigateNext = useChallengeStoreV2(selectCanNavigateNext)
  const canNavigatePrevious = useChallengeStoreV2(selectCanNavigatePrevious)
  const allAnswered = useChallengeStoreV2(selectAllAnswered)
  const isAdaptive = useChallengeStoreV2(selectIsAdaptiveStage)
  const isLastQuestion = useChallengeStoreV2(selectIsLastQuestion)
  const isLoadingQuestion = useChallengeStoreV2((state) => state.isLoadingQuestion)
  const isCompletingStage = useChallengeStoreV2((state) => state.isCompletingStage)
  const hasCurrentAnswer = useChallengeStoreV2(selectHasAnswer)
  const unansweredCount = useChallengeStoreV2(selectUnansweredCount)
  const otherUnansweredCount = useChallengeStoreV2(selectOtherUnansweredCount)
  const hasLeftLastQuestion = useChallengeStoreV2(selectHasLeftLastQuestion)

  // Store actions
  const toggleFocusMode = useChallengeStoreV2((state) => state.toggleFocusMode)
  const handleNavigatePrevious = useChallengeStoreV2((state) => state.handleNavigatePrevious)
  const handleNavigateNext = useChallengeStoreV2((state) => state.handleNavigateNext)
  const handleSubmitStage = useChallengeStoreV2((state) => state.handleSubmitStage)
  const openReviewModal = useChallengeStoreV2((state) => state.openReviewModal)
  const pauseChallenge = useChallengeStoreV2((state) => state.pauseChallenge)
  const finalAnswerWarningDismissed = useChallengeStoreV2(
    (state) => state.finalAnswerWarningDismissed
  )
  const openFinalAnswerConfirmation = useChallengeStoreV2(
    (state) => state.openFinalAnswerConfirmation
  )

  // Navigation handlers
  const onPrevious = useCallback(() => {
    if (canNavigatePrevious) {
      handleNavigatePrevious()
    }
  }, [canNavigatePrevious, handleNavigatePrevious])

  const onNext = useCallback(() => {
    handleNavigateNext()
  }, [handleNavigateNext])

  const onSubmit = useCallback(() => {
    // Surface a friendly "answers lock once submitted" confirmation first, unless the student has
    // opted out. The confirmation modal calls handleSubmitStage on confirm.
    if (!finalAnswerWarningDismissed) {
      openFinalAnswerConfirmation()
    } else {
      handleSubmitStage()
    }
  }, [finalAnswerWarningDismissed, openFinalAnswerConfirmation, handleSubmitStage])

  const onReview = useCallback(() => {
    openReviewModal()
  }, [openReviewModal])

  const onPause = useCallback(() => {
    pauseChallenge()
  }, [pauseChallenge])

  // Get stage type for badge
  const stageType =
    currentStage?.stageData?.factoryType ||
    currentStage?.configuration?.factoryType ||
    'EXPLORATORY'
  const isAdaptiveStage = stageType === 'ADAPTIVE'

  // Determine if we should show submit button (on last question or all answered)
  const showSubmit = isLastQuestion || allAnswered

  // Submit is only enabled when ALL questions are answered (including current)
  const canSubmit = allAnswered && hasCurrentAnswer

  // Fixed position: below main header (64px) + impersonation banner (40px) if active
  // Using fixed instead of sticky because parent has overflow-x-hidden which breaks sticky
  const topClass = isImpersonating ? 'top-[104px]' : 'top-16'

  return (
    <header
      className={`fixed left-0 right-0 lg:left-64 ${topClass} z-40 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 shadow-sm`}
    >
      <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-14 gap-1 sm:gap-2">
          {/* Left: Previous button */}
          <button
            onClick={onPrevious}
            disabled={!canNavigatePrevious}
            className={`
              flex items-center gap-1 px-2 sm:px-3 py-2 rounded-lg font-medium text-sm
              transition-all duration-200 shrink-0
              ${
                canNavigatePrevious
                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
              }
            `}
            aria-label="Previous question"
          >
            <ChevronLeftIcon />
            <span className="hidden md:inline">Prev</span>
          </button>

          {/* Center: Stage info + Timer + Progress */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 justify-center">
            {/* Stage indicator */}
            <span
              className={`hidden sm:inline px-2 py-0.5 text-xs font-medium rounded-full shrink-0 ${
                isAdaptiveStage
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
              }`}
            >
              {stageProgress.current}/{stageProgress.total}
            </span>

            {/* Timer */}
            <TimerDisplayV2 compact />

            {/* Progress */}
            <div className="flex items-center gap-1.5">
              <div className="hidden sm:block w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                {currentQuestionIndex + 1}/{progress.total}
              </span>
            </div>
          </div>

          {/* Right: Next/Submit + Review + Focus */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Pause button */}
            <button
              onClick={onPause}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg text-gray-500 dark:text-gray-400
                hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Pause test"
              title="Pause (P)"
            >
              <PauseIcon />
              <span className="hidden sm:inline text-xs">Pause</span>
            </button>

            {/* Calculator button (for math challenges) */}
            {window.__desmosLoaded__ !== false && (
              <button
                onClick={toggleCalculator}
                className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg transition-colors ${
                  isCalculatorOpen
                    ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                aria-label="Graphing Calculator"
                title="Graphing Calculator (Ctrl+G)"
              >
                <CalculatorIcon />
                <span className="hidden sm:inline text-xs">Calculator</span>
              </button>
            )}

            {/* Reference Sheet button (math challenges only) */}
            {isMathChallenge && (
              <button
                onClick={() => setIsReferenceSheetOpen(true)}
                className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg transition-colors ${
                  isReferenceSheetOpen
                    ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                aria-label="Math Reference Sheet"
                title="Math Reference Sheet"
              >
                <ReferenceSheetIcon />
                <span className="hidden sm:inline text-xs">Reference</span>
              </button>
            )}

            {/* Review button (both exploratory and adaptive) */}
            <button
              onClick={onReview}
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg text-gray-500 dark:text-gray-400
                hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Review all questions"
              title="Review (Ctrl+R)"
            >
              <GridIcon />
              <span className="hidden sm:inline text-xs">Review</span>
            </button>

            {/* Focus mode toggle */}
            <button
              onClick={toggleFocusMode}
              className={`flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg transition-all duration-200 ${
                isFocusMode
                  ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}
              title={isFocusMode ? 'Exit focus mode (Ctrl+F)' : 'Enter focus mode (Ctrl+F)'}
              aria-label={isFocusMode ? 'Exit focus mode' : 'Enter focus mode'}
            >
              {isFocusMode ? <MinimizeIcon /> : <FocusIcon />}
              <span className="hidden sm:inline text-xs">Focus</span>
            </button>

            {/* Next/Submit Button */}
            {showSubmit ? (
              <button
                onClick={onSubmit}
                disabled={isCompletingStage || !canSubmit}
                className={`
                  flex items-center gap-1 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm
                  transition-all duration-200 text-white
                  ${
                    canSubmit
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-sm hover:shadow-md'
                      : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                  }
                `}
                aria-label="Submit stage"
                title={!canSubmit ? 'Answer all questions to submit' : 'Submit stage'}
              >
                {isCompletingStage ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                ) : (
                  <>
                    <span>Submit</span>
                    {!canSubmit && unansweredCount > 0 && (
                      <span className="text-xs opacity-80 ml-1">({unansweredCount})</span>
                    )}
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={onNext}
                disabled={isLoadingQuestion || (isAdaptive && !hasCurrentAnswer)}
                className={`
                  flex items-center gap-1 px-2 sm:px-3 py-2 rounded-lg font-medium text-sm
                  transition-all duration-200
                  ${
                    isAdaptive && !hasCurrentAnswer
                      ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-sm hover:shadow-md'
                  }
                  disabled:opacity-50
                `}
                aria-label="Next question"
                title={
                  isAdaptive && !hasCurrentAnswer ? 'Select an answer to continue' : 'Next question'
                }
              >
                {isLoadingQuestion ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                ) : (
                  <>
                    <span className="hidden md:inline">Next</span>
                    <ChevronRightIcon />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Warning banner for unanswered questions */}
      {showSubmit &&
        !canSubmit &&
        unansweredCount > 0 &&
        (otherUnansweredCount > 0 ? (
          <div
            onClick={onReview}
            className="cursor-pointer bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-center"
          >
            <span className="text-sm text-amber-700 dark:text-amber-400">
              {otherUnansweredCount} other question{otherUnansweredCount > 1 ? 's' : ''} still
              unanswered —<span className="underline ml-1">click to review</span>
            </span>
          </div>
        ) : hasLeftLastQuestion ? (
          <div className="bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800 px-4 py-2 text-center">
            <span className="text-sm text-primary-700 dark:text-primary-400">
              Answer this question to submit
            </span>
          </div>
        ) : null)}

      {/* Mobile progress bar */}
      <div className="sm:hidden h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 transition-all duration-500"
          style={{ width: `${progress.percentage}%` }}
        />
      </div>

      {/* Desmos Calculator Overlay */}
      {isCalculatorOpen && (
        <Suspense fallback={null}>
          <DesmosCalculatorOverlay isOpen={isCalculatorOpen} onClose={closeCalculator} />
        </Suspense>
      )}

      {/* Math Reference Sheet */}
      {isReferenceSheetOpen && (
        <Suspense fallback={null}>
          <MathReferenceSheet
            isOpen={isReferenceSheetOpen}
            onClose={() => setIsReferenceSheetOpen(false)}
          />
        </Suspense>
      )}
    </header>
  )
})

ChallengeHeaderV2.displayName = 'ChallengeHeaderV2'

export default ChallengeHeaderV2
